import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, shareReplay } from 'rxjs';
import { IResponse } from '../../../shared/data-access/models/response.models';
import { Details } from '../models/details-model';
import { observe } from '../../../shared/utils/functions/observe.function';
import { UploadFile } from '../../../shared/data-access/models/fileupload.model';
import { UniversityDetails } from '../models/university-details.model';
import { DepartmentStatus } from '../models/departmentStatus.enum';
import { revertAction } from '../../../shared/data-access/models/revertAction.model';
import { FileUpdate } from '../models/fileUpdate.model';
import {EmailResponse} from '../../../shared/data-access/models/emailResponse.model';
import { FormGroup } from '@angular/forms';
import { ChangeRequests } from '../../../university-dashboard/data-access/models/change-request.model';
import { SnackBarMessage } from '../../../shared/enums/snackBarMessage';
import { SnackBarDuration } from '../../../shared/enums/snackBarDuration';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Roles } from '../../../authentication/data-access/models/auth.model';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  userRoles$: Observable<Roles[]> | undefined;
  constructor(private http: HttpClient, private snackBar: MatSnackBar) {}

  uploadPayment(file: any, applicationId: number, doctype: number = 3) {
    const body = { file, applicationId, doctype };

    return observe(this.http.post(`api/uploadPayment`, body));
  }

  sendEmailEmailToExec(
    applicationGuid: string,
  ): Observable<IResponse<EmailResponse>> {
    const body = { applicationGuid };
    return observe(this.http.post<EmailResponse>(`api/sendEmailToExecutive`, body));
  }

  updateApplicationStatus(
    status: string,
    applicationGuid: string,
    isRenewal?:boolean
  ): Observable<IResponse<number[]>> {
    const body = { status, applicationGuid, isRenewal };
    return observe(
      this.http.post<number[]>(`api/update-student-application-status`, body),
    );
  }

  rejectApplication(
    status: string,
    applicationGuid: string,
    reason: string,
    motivation: string,
  ): Observable<IResponse<{ name: string; email: string }>> {
    const body = { status, applicationGuid, reason, motivation };
    return observe(
      this.http.post<{ name: string; email: string }>(
        `api/reject-student-application`,
        body,
      ),
    );
  }

  updateBursaryAmount(
    applicationGuid: string,
    newAmount: number,
  ): Observable<IResponse<string[]>> {
    const body = { applicationGuid, newAmount };
    return observe(
      this.http.put<string[]>(
        `api/updateApplicationAmount?applicationGuid=${applicationGuid}&newAmount=${newAmount}`,
        body,
      ),
    );
  }

  getYearsOfFunding(): Observable<IResponse<number[]>> {
    return observe(this.http.get<number[]>('api/funding-years'));
  }

  getStudentYearsOfFunding(): Observable<IResponse<number[]>> {
    return observe(this.http.get<number[]>('api/student-funding-years'));
  }
  emailFailedApplication(applicationGuid: string):Observable<IResponse<any>>  {
    const body = { applicationGuid };
    return observe(
      this.http.post<any>(`api/emailFailedStudentApplication`, body),
    );
  }
  getUniversityCardData(year: number,isWarmUp:boolean = false,viewTypeFlag:string) {
    return observe(
      this.http.get<any>(`api/university-card-data/?year=${year}&viewTypeFlag=${viewTypeFlag}&isWarmUp=${isWarmUp}`),
    );
  }
  getAllUniversities(): Observable<IResponse<UniversityDetails[]>> {
    return observe(this.http.get<UniversityDetails[]>('api/universities'));
  }
  updateUniversityDepartment(oldDepartmentName: string, newDepartmentName: string, universityToUpdate: string, facultyName: string):Observable<IResponse<any>> {
    const body={
     oldDepartmentName,
     newDepartmentName,
     universityToUpdate,
     facultyName
    }
    return observe(this.http.put(`api/updateUniversityDepartment`, body));
  }
  deleteUniversityDepartment(departmentName: string, universityName: string, facultyName: string) {
    const status=DepartmentStatus.DELETE
    const body = {
      departmentName,
      universityName,
      facultyName,
      status
    };
    return observe(this.http.put(`api/updateUniversityDepartmentStatus`, body));
  }
  toogleUniversityDepartment(departmentName: string, universityName: string,facultyName: string,enabled:boolean) {
    const status=enabled? DepartmentStatus.DISABLED: DepartmentStatus.ACTIVE
    const body = {
      departmentName,
      universityName,
      facultyName,
      status
    };
    return observe(this.http.put(`api/updateUniversityDepartmentStatus`, body));
  }
  updateUserDetails(userId: string, customAttributes: FormGroup, applicationGuidDetailsForm: FormGroup, profile: string): Observable<IResponse<{success: boolean}>> {
    const body = {
      oid: userId,
      customAttributes: customAttributes,
      applicationGuidDetailsForm: applicationGuidDetailsForm,
      updatedProfile: profile
    };
    return this.http.put(`api/user`, body, );
  }

  createUser( customAttributes: any): Observable<IResponse<{status: number}>> {
    return observe(this.http.post<{status: number}>(`api/createAdminUser`,customAttributes, ));
  }

  revertApplication(revertApplication: revertAction) {
    return observe(this.http.post<{status:number}>(`api/revert-application`, revertApplication))
  }

  updateDocument(data:FileUpdate) {
    return observe(this.http.post<{status:number}>(`api/update-student-document`, data))
  }

  terminateBursary(
    status: string,
    applicationGuid: string,
    reason: string,
    motivation: string,
  ): Observable<IResponse<{ name: string; email: string }>> {
    const body = { status, applicationGuid, reason, motivation };
    return observe(this.http.post<{ name: string; email: string }>(`api/student-terminate-bursary`, body));
  }

  requestChanges(
    applicationGuid: string,
    applicantName: string,
    studentEmail: string,
    changeRequests: ChangeRequests[],
    recipientType: Roles = Roles.STUDENT,
  ): Observable<IResponse<any>> {
    const body = { applicationGuid, applicantName, studentEmail, changeRequests, recipientType};
    return observe(this.http.post<{ name: string; email: string }>(`api/request-application-changes`, body));
  }

  getRoles(): Observable<Roles[]> {
    if (!this.userRoles$) {
      this.userRoles$ = this.http.get<Roles[]>(`api/user-roles`).pipe(
        shareReplay(1),
        catchError(() => {
          this.snackBar.open(
            SnackBarMessage.FAILURE,
            SnackBarMessage.CLOSE,
            { duration: SnackBarDuration.DURATION }
          );
          return of([]);
        })
      );
    }
    return this.userRoles$;
  }

  updateStudentGradeAverage(applicationGuid: string, docBlobName: string, documentName: string, average: number): Observable<IResponse<any>> {
    const body = { applicationGuid, docBlobName, documentName, average };
    return observe(this.http.put('api/update-grade-average', body));
  }
}
